import pluralize from "pluralize";
import {useState} from "react";
import {useNavigate} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCheckoutContext} from "@/components/context/hooks.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {humanFriendlyInt, shortCurrency} from "@/lib/format.tsx";
import {PlanLimits} from "@/lib/types/accounts.ts";
import {AddedValue, BillingPeriod, CalculatedBillingData, Discount, Plan} from "@/lib/types/billing.ts";
import {Discounts} from "@/pages/billing/checkout/components/Discounts.tsx";
import {PromocodeInput} from "@/pages/billing/checkout/components/PromocodeInput.tsx";
import {QuoteModal} from "@/pages/billing/checkout/components/QuoteModal.tsx";
import {PURCHASE_ITEM_NAMES} from "@/pages/billing/utils.ts";

export interface PurchaseSummaryProps {
  calculatedData?: CalculatedBillingData;
  billingPeriod?: BillingPeriod;
  plan?: Plan;
  addedValues: AddedValue[];
  onPromoCodeSuccess: ((code: string) => void) | (() => void);
  formId: string;
  isCustomer: boolean;
  isUpgrade: boolean;
}

export const PurchaseSummary = ({
  calculatedData,
  addedValues,
  plan,
  billingPeriod,
  isCustomer,
  isUpgrade, // upgrade subscription (plan), no addons, no duration change
  onPromoCodeSuccess,
  formId,
}: PurchaseSummaryProps) => {
  const allowPromocode = isUpgrade;
  const {requestInProgress} = useCheckoutContext();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const navigate = useNavigate();
  if (!calculatedData) {
    return (
      <div className="d-block w-100 justify-content-center align-items-center">
        <LoadingContainer />
      </div>
    );
  }

  const miniQuoteDiscounts = (calculatedData.discounts || []).filter((el) => el.mini_quote);
  const commonDiscounts = miniQuoteDiscounts.filter((el) => !el.is_promo);
  const promoDiscounts = miniQuoteDiscounts.filter((el) => el.is_promo);
  const hasOneTimePromo = miniQuoteDiscounts.filter((el) => el.is_promo && !el.recurring).length > 0;
  const isAnnual = billingPeriod === BillingPeriod.YEAR;
  const isSwitchToAnnual = addedValues.find((av) => av.limit_field === "annual_billing") && isAnnual;
  let totalCostLabel, totalPrice, monthlySavings, totalPricePerAnnum;
  if (isCustomer) {
    if (isUpgrade) {
      // upgrade subscription (plan), no addons, no duration change
      totalPrice = calculatedData.total_due_today;
      totalCostLabel = "Total Due Today";
      monthlySavings = 0;
      totalPricePerAnnum = calculatedData.price_monthly * 12;
    } else {
      // purchase addons or switch to annual
      totalCostLabel = "Total Per Month";
      totalPrice = calculatedData.added_cost;
      monthlySavings = calculatedData.added_cost_full - totalPrice;
      totalPricePerAnnum = isSwitchToAnnual ? calculatedData.price_monthly * 12 : calculatedData.added_cost * 12;
    }
  } else {
    // initial subscription for an FT to a plan
    if (hasOneTimePromo) {
      totalCostLabel = "Total Due Today";
      totalPrice = calculatedData.total_due_today;
      monthlySavings = calculatedData.monthly_subtotal - calculatedData.price_monthly;
    } else {
      totalCostLabel = "Total Per Month";
      totalPrice = calculatedData.price_monthly;
      monthlySavings = calculatedData.monthly_subtotal - calculatedData.price_monthly;
    }
    totalPricePerAnnum = calculatedData.price_monthly * 12;
  }

  return (
    <div className="white-block white-block-border p-3 rounded rounded-3">
      <div className="d-flex justify-content-between align-items-baseline">
        <h4 className="fst-normal fw-bold fs-6">Purchase Summary</h4>
        {!isSwitchToAnnual && (
          <button className="btn btn-link fst-italic p-0" type="button" onClick={() => setShowQuoteModal(true)}>
            <small>View Quote</small>
          </button>
        )}
      </div>
      <hr className="pulse-color-gray-500 my-3" />

      {isCustomer && isUpgrade && (
        <div className="row mb-2">
          <div className="col-8 col-lg-8">+ {calculatedData.base_plan}</div>
          <div className="col-4 col-lg-4 text-end">
            <strong>{shortCurrency(calculatedData.base_price, 2)}</strong>
          </div>
        </div>
      )}
      {!isCustomer && (
        <div className="row mb-2">
          <div className="col-8 col-lg-8">Base Price: {calculatedData.base_plan}</div>
          <div className="col-4 col-lg-4 text-end">
            <strong>{shortCurrency(calculatedData.base_price, 2)}</strong>
          </div>
        </div>
      )}
      {addedValues.map((av) => {
        const limitName = PURCHASE_ITEM_NAMES[av.limit_field as keyof PlanLimits];
        const limitPrice = calculatedData.units_prices.find((el) => el.name === av.limit_field)?.price || 0;
        const itemCostVerbose = av.amount > 0 ? shortCurrency(limitPrice * av.amount, 2) : "";
        return limitName !== null && av.amount ? (
          <div className="row mb-2" key={"invoice-line-" + av.limit_field}>
            <div className="col-8 col-lg-8">
              +{av.amount > 0 && av.limit_field !== "annual_billing" ? humanFriendlyInt(av.amount) : ""}{" "}
              {pluralize(limitName, av.amount)}
            </div>
            <div className="col-4 col-lg-4 text-end">
              <strong>{itemCostVerbose}</strong>
            </div>
          </div>
        ) : null;
      })}
      {miniQuoteDiscounts.length ? <hr className="pulse-color-gray-500 my-3" /> : false}
      <Discounts discounts={commonDiscounts} />
      {allowPromocode ? (
        <>
          <div className="row mt-3">
            <div className="col-12">
              <PromocodeInput onChange={onPromoCodeSuccess} billingPeriod={billingPeriod} plan={plan} />
            </div>
          </div>
          <Discounts discounts={promoDiscounts} />
        </>
      ) : null}
      <hr className="pulse-color-gray-500 my-3" />
      {monthlySavings > 0 && (
        <Discounts
          discounts={[{title: "Monthly Savings", percent: 0, value: monthlySavings * -1, highlight: true} as Discount]}
        />
      )}
      {!isSwitchToAnnual && (
        <div className="row mb-2">
          <div className="col-8 col-lg-8">
            <strong>{totalCostLabel}</strong>
          </div>
          <div className="col-4 col-lg-4 text-end">
            <strong>{shortCurrency(totalPrice, 2)}</strong>
          </div>
        </div>
      )}
      {isAnnual ? (
        <div className="row mb-4">
          <div className="col-8 col-lg-8">
            <strong>Billed Annually</strong>
          </div>
          <div className="col-4 col-lg-4 text-end">
            <strong>{shortCurrency(totalPricePerAnnum, 2)}</strong>
          </div>
        </div>
      ) : (
        (isUpgrade || !isCustomer) && (
          <div className="row mb-4">
            <div className="col-8 col-lg-8">
              <strong>Recurring Monthly Cost</strong>
            </div>
            <div className="col-4 col-lg-4 text-end">
              <strong>{shortCurrency(calculatedData.price_monthly, 2)}</strong>
            </div>
          </div>
        )
      )}
      <div className="d-none d-md-block">
        {isCustomer && (
          <div className="row mb-3 mt-1">
            <div className="col-12 lh-sm">
              <small className="text-muted">
                * Additional charges are calculated pro-rata for the remainder of the current billing cycle.
              </small>
            </div>
          </div>
        )}
        <div className="row mb-2">
          <div className="col-12">
            <Button type="submit" className="w-100" form={formId} disabled={requestInProgress}>
              {requestInProgress ? "Working..." : (isCustomer ? "Pay" : "Subscribe") + " Now"}
            </Button>
          </div>
        </div>
        <div className="row">
          <div className="col-12 text-center d-flex flex-column align-items-center">
            <Button
              variant={"secondary"}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate(-1);
              }}
              disabled={requestInProgress}
            >
              Back
            </Button>
          </div>
        </div>
      </div>
      {calculatedData && (
        <QuoteModal
          plan={plan}
          calculatedData={calculatedData}
          addedValues={addedValues}
          isCustomer={isCustomer}
          isUpgrade={isUpgrade}
          isAnnual={isAnnual}
          show={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
        />
      )}
    </div>
  );
};
